import { EmptyState, ErrorState, Input, RoomIcon, SearchIcon } from '@bp/ui-components';
import classNames from 'classnames';
import { EventType } from 'matrix-js-sdk';
import type { RoomMessageEventContent } from 'matrix-js-sdk/lib/types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowDimensions } from 'utils/dimensions';
import { FragmentType } from '../../client/gql';
import { UseDMRoomsFragment, useDMRooms } from '../../matrix/hooks/useDMRooms';
import { useMatrixStatus } from '../../matrix/hooks/useMatrixStatus';
import { BpCard } from '../BpCard/BpCard';
import styles from './MatrixChat.module.scss';
import { MatrixConversation } from './MatrixConversation/MatrixConversation';
import { MatrixRoomList } from './MatrixRoomList/MatrixRoomList';

type MatrixChatProps = {
  matrixRoomId?: string;
  query: FragmentType<typeof UseDMRoomsFragment> | undefined;
};

export const MatrixChat = ({ matrixRoomId, query }: MatrixChatProps) => {
  const { t } = useTranslation();
  const rooms = useDMRooms({ data: query });
  const { hasFatalError, showSyncingUI } = useMatrixStatus();

  const [search, setSearch] = useState<string>('');
  const [roomId, setRoomId] = useState<string | null>(null);

  const { width } = useWindowDimensions();
  const isSmall = width < 770;

  useEffect(() => {
    if (matrixRoomId) {
      setRoomId(matrixRoomId);
    }
  }, [matrixRoomId]);

  const filteredRooms = useMemo(() => {
    if (!search || search === '') {
      return rooms;
    }

    return rooms?.filter(({ roomInformation, timeline }) => {
      const searchLower = search.toLowerCase();

      // Suche im Raumnamen
      if (roomInformation.displayName.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Suche in den Nachrichten
      return timeline?.some((event) => {
        if (event.getType() === EventType.RoomMessage) {
          const content = event.getContent<RoomMessageEventContent>();
          if (content.body && content.body.toLowerCase().includes(searchLower)) {
            return true;
          }
        }
        return false;
      });
    });
  }, [search, rooms]);

  if (hasFatalError) {
    return (
      <div className={styles.messenger}>
        <BpCard fitParent noPadding>
          <ErrorState title={t('matrix.noAvailable')} />
        </BpCard>
      </div>
    );
  }

  if (showSyncingUI) {
    return (
      <div className={styles.messenger}>
        <BpCard fitParent noPadding>
          <EmptyState fitParent icon={<RoomIcon />} title={t('matrix.syncingMessages')} />
        </BpCard>
      </div>
    );
  }

  return (
    <div className={styles.messenger}>
      <div className={styles.wrapper}>
        <div className={styles['side-bar']}>
          <div className={styles.search}>
            <Input
              className={styles.input}
              onChange={(event) => setSearch(event.target.value)}
              value={search}
              name='search'
              prefix={<SearchIcon />}
              dense
              placeholder={t('common.search')}
            />
          </div>
          <div className={styles['rooms-list']}>
            <MatrixRoomList onSelect={(roomId) => setRoomId(roomId)} selected={roomId} rooms={filteredRooms} />
          </div>
        </div>
        <div
          className={classNames(styles.chat, {
            [styles['fly-out']]: isSmall && roomId,
          })}
        >
          <div className={styles.conversation}>
            {roomId ? (
              <MatrixConversation roomId={roomId} onBackClick={() => setRoomId(null)} />
            ) : (
              <EmptyState fitParent icon={<RoomIcon />} title={t('messages.chooseRoom')} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
